import type { Balance, RoundSnapshot, SourceMode } from '@urbanrats/contracts';
import type { ConnectionState } from '../gateway/index.ts';
import { useI18n, type MessageKey } from '../i18n/index.ts';
import { fmtMoney } from '../lib/format.ts';
import { Approx } from './Approx.tsx';

interface Props {
  round: RoundSnapshot | null;
  sourceMode: SourceMode;
  connection: ConnectionState;
  balance: Balance | null;
  onOpen: (panel: 'history' | 'rules' | 'settings' | 'source' | 'wallet' | 'rewards' | 'ranks') => void;
  showRewards?: boolean;
  showRanks?: boolean;
  gameTitle: string;
  onLobby?: () => void;
  /** Sound: shown as a speaker button in the bar (browsers only allow sound after a tap). */
  muted?: boolean;
  onToggleSound?: () => void;
  /** Partner demo launch: play money only. */
  funPlay?: boolean;
  /** Real-money session: no DEMO tag on the balance. */
  realMoney?: boolean;
}

const CONN_ICON: Record<ConnectionState, string> = {
  connected: '●',
  reconnecting: '◐',
  offline: '○',
};

export function TopBar({ round, sourceMode, connection, balance, onOpen, gameTitle, onLobby, showRewards = false, showRanks = false, muted = true, onToggleSound, funPlay = false, realMoney = false }: Props) {
  const { t, intl } = useI18n();
  return (
    <header className="topbar">
      <div className="brand">
        <span className="logo" aria-hidden>
          ◆
        </span>
        <span className="brand-name">UrbanRats</span>
        <span className="brand-game" data-testid="game-title">
          {gameTitle}
        </span>
      </div>
      <div className="topbar-status" role="group" aria-label="Status">
        <span className={`chip mode mode-${sourceMode}`} title={sourceMode === 'SIMULATION' ? t('mode.SIMULATION.hint') : undefined} data-testid="source-mode">
          <span aria-hidden>▲</span> {t(`mode.${sourceMode}` as MessageKey)}
        </span>
        {funPlay && (
          <span className="chip fun-play" title={t('funPlay.hint')} data-testid="fun-play">
            {t('funPlay.tag')}
          </span>
        )}
        {round && (
          <span className="chip round-id" title={round.gameRoundId}>
            {t('app.round', { id: round.gameRoundId.slice(-6) })}
          </span>
        )}
        <span className={`chip conn conn-${connection}`} role="status" data-testid="connection">
          <span aria-hidden>{CONN_ICON[connection]}</span> {t(`conn.${connection}` as MessageKey)}
        </span>
        {onToggleSound && (
          <button type="button" className={`sound-btn ${muted ? 'off' : 'on'}`} onClick={onToggleSound} aria-pressed={!muted} aria-label={t(muted ? 'sound.turnOn' : 'sound.turnOff')} data-testid="sound-toggle">
            <span aria-hidden>{muted ? '🔇' : '🔊'}</span>
          </button>
        )}
        <span className="chip balance" aria-label={t(realMoney ? 'balance.real' : 'balance.demo')} data-testid="balance">
          {!realMoney && <span className="demo-tag">{t('balance.demoTag')}</span>} {balance ? fmtMoney(balance.balance, intl) : '—'}
        </span>
        {balance && <Approx money={balance.balance} className="chip" />}
      </div>
      <nav className="topbar-actions" aria-label="Game menu">
        {onLobby && (
          <button type="button" className="ghost-btn" onClick={onLobby}>
            ← {t('lobby.back')}
          </button>
        )}
        <button type="button" className="ghost-btn" onClick={() => onOpen('wallet')}>
          {t('nav.wallet')}
        </button>
        {showRewards && (
          <button type="button" className="ghost-btn" onClick={() => onOpen('rewards')}>
            {t('nav.rewards')}
          </button>
        )}
        {showRanks && (
          <button type="button" className="ghost-btn" onClick={() => onOpen('ranks')} data-testid="nav-ranks">
            {t('nav.ranks')}
          </button>
        )}
        <button type="button" className="ghost-btn" onClick={() => onOpen('source')}>
          {t('nav.source')}
        </button>
        <button type="button" className="ghost-btn" onClick={() => onOpen('history')}>
          {t('nav.history')}
        </button>
        <button type="button" className="ghost-btn" onClick={() => onOpen('rules')}>
          {t('nav.rules')}
        </button>
        <button type="button" className="ghost-btn" onClick={() => onOpen('settings')}>
          {t('nav.settings')}
        </button>
      </nav>
    </header>
  );
}
